import { Invoice, SKU } from '../../../redux/billing/billing.types';
import { Company } from 'src/pages/billing/types/billing.types';

export const formatCurrency = (amount: number | null): string => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

export const formatPercentage = (percent: number, toFixed: number = 1): string => {
  return `${(percent * 100).toFixed(toFixed)}%`;
};

export const formatLastSynced = (updatedAt: string | null): string => {
  if (!updatedAt) {
    return 'Not yet synced';
  }

  const now = new Date();
  const updated = new Date(updatedAt);
  const diffInSeconds = Math.floor((now.getTime() - updated.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Synced now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Synced ${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Synced ${diffInHours} hr${diffInHours !== 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  return `Synced ${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
};

export const cleanNumericInput = (value: string): string => {
  return value.replace(/[^\d.-]/g, '');
};

/** Display text when an invoice has no company assigned */
export const UNASSIGNED_INVOICE_LABEL = 'NO COMPANY ASSIGNED';

export const getCompanyName = (companyId: string, companyLookup: Record<string, Company>, _invoices: Invoice[] = []): string => {
  if (!companyId || !companyLookup[companyId]) {
    return UNASSIGNED_INVOICE_LABEL;
  }

  const company = companyLookup[companyId];
  return company?.company_name || companyId;
};

export const getInvoiceChannelDisplay = (invoice: Invoice): string => {
  if (invoice.channel_display) {
    return invoice.channel_display;
  }

  if (!invoice.invoice_line_items || invoice.invoice_line_items.length === 0) {
    return '-';
  }

  const channels = [...new Set(invoice.invoice_line_items.map(item => item.channel).filter(Boolean))];

  if (channels.length === 0) {
    return '-';
  }

  if (channels.length === 1) {
    return channels[0].charAt(0).toUpperCase() + channels[0].slice(1);
  }

  return 'Multiple';
};

export const getInvoiceTotals = (invoice: Invoice) => {
  if (!invoice.invoice_line_items?.length && invoice.list_impressions != null) {
    const amountToBill =
      invoice.amount_to_bill != null && !Number.isNaN(Number(invoice.amount_to_bill))
        ? Number(invoice.amount_to_bill)
        : invoice.subtotal ?? 0;
    return {
      impressions: invoice.list_impressions,
      adSpend: 0,
      amountToBill,
    };
  }

  return invoice.invoice_line_items.reduce(
    (totals, item) => ({
      impressions: totals.impressions + (item.qty ?? 0),
      adSpend: totals.adSpend + (item.advertiser_spend_override ?? item.advertiser_spend),
      amountToBill: totals.amountToBill + (item.do_not_bill || item.hide ? 0 : (item.amount_to_bill || 0)),
    }),
    { impressions: 0, adSpend: 0, amountToBill: 0 }
  );
};

export const calculateInterconnectedValues = (field: string, value: number, lineItem: any, skus: any = null) => {
  const updates: Record<string, any> = {};
  const spend = lineItem.advertiser_spend_override ?? lineItem.advertiser_spend;
  const marginPct = lineItem.margin_pct_override ?? lineItem.margin_pct;

  switch (field) {
    case 'advertiser_spend_override': {
      const newMargin = value * marginPct;
      updates.margin = newMargin;
      updates.amount_to_bill = spend + newMargin;
      break;
    }

    case 'margin_pct_override': {
      // If margin percentage changes, calculate new margin
      const newMargin = spend * value;
      updates.margin = newMargin;
      updates.amount_to_bill = spend + newMargin;
      break;
    }

    case 'sku_override': {
      if (skus) {
        const sku = skus.find((sku: SKU) => sku.id === String(value));
        if (sku) {
          updates.sku_override = value;

          switch (sku.bill_type) {
            case 'bill_in_full':
              updates.amount_to_bill = spend + lineItem.margin;
              break;
            case 'margin_only':
              updates.amount_to_bill = lineItem.margin;
              break;
            case 'pass_through':
              updates.amount_to_bill = spend;
              break;
          }
        }
      }
      break;
    }

  }

  return updates;
};

export const isItemEditable = (invoice: Invoice, lineItem?: any): boolean => {
  // If approved, synced, or paid, not editable
  const invoiceStatus = invoice.invoice_status?.toLowerCase();
  if (invoiceStatus === 'synced' || invoiceStatus === 'approved' || invoiceStatus === 'paid') {
    return false;
  }
  if (lineItem && (lineItem.invoice_line_item_status?.toLowerCase() === 'synced' || lineItem.invoice_line_item_status?.toLowerCase() === 'approved')) {
    return false;
  }
  return true;
};

export const getTermNameById = (termId: string, termsLookup: Record<string, any>): string => {
  const term = termsLookup[termId];
  return term ? term.name : termId;
};

export const getSkuNameById = (skuId: string, skusLookup: Record<string, SKU>): string => {
  const sku = skusLookup[skuId];
  if (!sku) return skuId;
  const code = sku.sku?.trim();
  const name = sku.name?.trim();
  if (code && name && code.toUpperCase() !== name.toUpperCase()) {
    return `${code} — ${name}`;
  }
  return name ?? code ?? skuId;
};
